import * as assert from 'assert';
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { CONSTANTS } from '../../assets/UIConstants';
import { CSSProperties } from 'react';
import { AreaGraph, NumericRange } from '../components/Graphs/AreaGraph';
import { PieChart, Pie, Cell } from 'recharts';
import { RouteComponentProps } from 'react-router';
import * as moment from 'moment';
import { computed } from 'mobx';

interface AllowedParams {
    dataPoints?: IDataPoint[];
    width?: number;
    height?: number;
    type?: string;
    color?: string;
    size?: string;
    hasGrid?: boolean;
    innerRadius?: number;
    outerRadius?: number;
    showYAxis?: boolean;
    maxYValue?: number;
    yAxisTickUnit?: string;
    goodValueComparisionValue?: number;
    goodValueComparision?: Comparision;
}

export enum Comparision {
    LESS_THAN = '<',
    GREATER_THAN = '>',
}

interface JsonParams {
    dataPoints?: IDataPoint[];
}

interface Props extends RouteComponentProps<AllowedParams> {
}

interface IDataPoint {
    x: number;
    y: number;
}

const DOT_SIZE_SMALL: number = 2;
const DOT_SIZE_MEDIUM: number = 4;
const DOT_SIZE_LARGE: number = 6;

@inject('store')
@observer
export class Public extends React.Component<Props> {
    private params: Partial<AllowedParams> = {};

    public constructor(props: Props) {
        super(props);
        this.loadUrlParams();
    }

    private loadUrlParams(): void {
        assert(
            typeof this.props.location.search === 'string',
            'Public#loadUrlParams - invalid query params string.'
        );

        const parsedQueryParams = this.getUrlSearchParams(this.props.location.search);

        this.params.width = this.getIntParam('width', parsedQueryParams);
        this.params.height = this.getIntParam('height', parsedQueryParams);
        this.params.color = this.getStringParam('color', parsedQueryParams);
        this.params.type = this.getStringParam('type', parsedQueryParams) || 'line';
        this.params.size = this.getStringParam('size', parsedQueryParams);
        this.params.hasGrid = this.getBooleanParam('hasGrid', parsedQueryParams);
        this.params.innerRadius = this.getIntParam('innerRadius', parsedQueryParams);
        this.params.outerRadius = this.getIntParam('outerRadius', parsedQueryParams);
        this.params.maxYValue = this.getIntParam('maxYValue', parsedQueryParams);
        this.params.showYAxis = this.getBooleanParam('showYAxis', parsedQueryParams);
        this.params.yAxisTickUnit = this.getStringParam('yAxisTickUnit', parsedQueryParams);
        this.params.goodValueComparision = this.getStringParam('goodValueComparision', parsedQueryParams) as Comparision;
        this.params.goodValueComparisionValue = this.getIntParam('goodValueComparisionValue', parsedQueryParams);

        const jsonParams = this.getJsonParam('json', parsedQueryParams);

        if (undefined != jsonParams && jsonParams.hasOwnProperty('dataPoints')) {
            if (Array.isArray(jsonParams.dataPoints)) {
                this.params.dataPoints = jsonParams.dataPoints;
            }
        }
    }

    private getUrlSearchParams(paramString: string) {

        const splitParameterStrings = paramString.substr(1).split('&'); // ["width=154", "size=medium", ...etc]
        let out = {};

        splitParameterStrings.forEach(param => {
            const splitByKeyAndValue = param.split('='); //["color", "%23FFA129"]
            const key = splitByKeyAndValue[0];
            const value = splitByKeyAndValue[1];
            out[key] = decodeURIComponent(value);
        });

        return out;
    }

    private getIntParam(keyName: string, paramsObject: {}): number | undefined {
        const value: string | null = paramsObject[keyName];

        if (typeof value === 'string') {
            const out: number = parseInt(value);
            if (isNaN(out)) {
                return undefined;
            }
            return out;
        }
        return undefined;
    }

    private getJsonParam(keyName: string, paramsObject: {}): JsonParams | undefined {
        const urlEncodedJson = paramsObject[keyName];

        if (typeof urlEncodedJson === 'string') {
            const decoded = decodeURIComponent(urlEncodedJson);
            const parsed = JSON.parse(decoded);
            if (null != parsed && typeof parsed === 'object') {
                return parsed;
            }
        }

        return undefined;
    }

    private getStringParam(keyName: string, paramsObject: {}): string | undefined {
        const value: string | null = paramsObject[keyName];
        if (typeof value !== 'string') {
            return undefined;
        }
        return value;
    }

    private getBooleanParam(keyName: string, paramsObject: {}): boolean | undefined {
        const value: string | null = paramsObject[keyName];
        if (typeof value === 'string') {
            if (value === 'true') {
                return true;
            } else if (value === 'false') {
                return false;
            } else {
                return undefined;
            }
        }
        return undefined;
    }

    private getDotRadius(size: string | undefined): number | undefined {
        let out: number | undefined = undefined;
        switch (size) {
            case 'small':
                out = DOT_SIZE_SMALL;
                break;
            case 'medium':
                out = DOT_SIZE_MEDIUM;
                break;
            case 'large':
                out = DOT_SIZE_LARGE;
                break;
            default:
                out = undefined;
        }

        return out;
    }

    protected generateXLabel(value: number): string {
        const date = moment(value);
        return date.format('M/DD');
    }

    @computed protected get graphDataPoints(): IDataPoint[] {
        return this.params.dataPoints || [];
    }

    @computed protected get yAxisRange(): NumericRange | undefined {
        console.log('hello?');
        if (this.graphDataPoints.length >= 1) {
            let maxValue: number = this.graphDataPoints[0].y;
            this.graphDataPoints.forEach((entry) => {
                if (entry.y > maxValue) {
                    maxValue = entry.y;
                }
            });
            return {
                min: 0,
                max: this.params.maxYValue || Math.ceil(maxValue * 1.15)
            };
            // sets maximum value on y axis to 15% more than the highest value in the measurements.
            // this has no functional implications, it just ensures a consistent visual scale for our graphs.
        }
        return undefined;
    }

    public render(): React.ReactNode {

        const isSmallGraph = this.params.size === 'small';
        console.log('COMPARE PROPS: GOOD VALUES ARE', this.params.goodValueComparision, this.params.goodValueComparisionValue);
        return (
            <div style={containerStyle}>
                {this.params.type === 'pie' &&
                    <div style={this.pieChartContainerStyle}>
                        <PieChart width={this.params.width} height={this.params.height}>
                            <Pie
                                data={this.graphDataPoints}
                                dataKey="value"
                                cx="50%"
                                cy="50%"
                                innerRadius={this.params.innerRadius || 80}
                                outerRadius={this.params.outerRadius || 100}
                                label={false}
                                startAngle={285}
                                endAngle={60}
                            >
                                {this.graphDataPoints.map((entry, index) =>
                                    <Cell key={index} fill={COLORS[index % COLORS.length]} stroke={'transparent'} />
                                )}
                            </Pie>
                        </PieChart>
                    </div>
                }

                {this.params.type === 'line' &&
                    <AreaGraph
                        dataPoints={this.graphDataPoints}
                        width={this.params.width}
                        height={this.params.height}
                        accentColor={this.params.color}
                        hasGrid={this.params.hasGrid}
                        showYAxis={this.params.showYAxis}
                        dotRadius={this.getDotRadius(this.params.size)}
                        margin={{ top: 30, left: 25, right: 25, bottom: -10 }}
                        tickStyle={{ fontSize: 11 }}
                        hasSmallFinalDot={isSmallGraph}
                        xAxisTickFormatter={this.generateXLabel}
                        yAxisRange={this.yAxisRange}
                        yAxisTickUnit={this.params.yAxisTickUnit}
                        goodValueComparisionValue={this.params.goodValueComparisionValue}
                        goodValueComparision={this.params.goodValueComparision}
                    />
                }
            </div>
        );
    }

    pieChartContainerStyle: React.CSSProperties = {
        width: (this.params.width || 200),
        height: (this.params.height || 200),
        position: CONSTANTS.POSITION.RELATIVE,
        backgroundColor: 'transparent',
        marginTop: -45,
    };
}

const containerStyle: CSSProperties = {
    backgroundColor: 'transparent',
    flexGrow: CONSTANTS.FLEX_GROW.ONE,
};

const COLORS = [CONSTANTS.COLORS.WHITE, CONSTANTS.COLORS.THEME_BLUE_DISABLED];


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/pages/Public.tsx